import type { ReactNode } from 'react';
import styles from './FilterBar.module.css';

interface Tab {
  label: string;
  value: string;
  count?: number;
}

interface FilterBarProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Desplegables de filtro (Estado, Prioridad, Equipo, Técnico). Solo los
   * pinta `layout="stacked"`; la barra `inline` del asistente no los tiene.
   */
  filters?: ReactNode;
  /** Texto a la derecha de la fila de filtros: «26 tickets · 6 sin asignar». */
  meta?: ReactNode;
  /**
   * `inline` (por defecto) — pestañas y buscador en una fila. Es lo que usa
   * la bandeja del asistente y no se toca.
   *
   * `stacked` — pestañas a la izquierda y buscador + filtros + contador a la
   * derecha, TODO en una fila (hasta el 2026-09-25 iban en dos). Los dos ejes
   * —vistas de trabajo frente a atributos del ticket— se siguen distinguiendo
   * por la forma del control y por el lado de la barra, no por la fila.
   */
  layout?: 'inline' | 'stacked';
}

export function FilterBar({
  tabs,
  activeTab,
  onTabChange,
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  filters,
  meta,
  layout = 'inline',
}: FilterBarProps) {
  const tabList = (
    <div className={styles.tabs}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          className={`${styles.tab} ${activeTab === tab.value ? styles.tabActive : ''}`}
          onClick={() => onTabChange(tab.value)}
        >
          {tab.label}
          {tab.count !== undefined && <span className={styles.tabCount}>{tab.count}</span>}
        </button>
      ))}
    </div>
  );

  const searchInput = (
    <input
      type="text"
      className={styles.search}
      placeholder={searchPlaceholder}
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
    />
  );

  if (layout === 'stacked') {
    // Las pestañas van PRIMERO, a la izquierda: son la pregunta que se hace
    // antes («¿qué estoy mirando?»), y los desplegables afinan lo que ya se
    // eligió. Hasta el 2026-09-25 iban en una segunda fila debajo — ver el
    // porqué del cambio en `.barStacked` (FilterBar.module.css).
    return (
      <div className={`${styles.bar} ${styles.barStacked}`}>
        {tabList}
        <div className={styles.controlRow}>
          {searchInput}
          {filters}
          {meta && <span className={styles.meta}>{meta}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.bar}>
      {tabList}
      {searchInput}
    </div>
  );
}
